import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import type {
	AiWeddingQualityId,
	AiWeddingTemplateId,
} from "./ai-wedding-preset";

/**
 * /ai-wedding-video-generator 样片区的片子；第一条同时是工作台右栏唯一的 Example。
 *
 * 外部片 onSite false，不进 VideoObject，卡上不标来源和模型，来源只记在这里和
 * packages/scripts/ai-wedding-showcase/README.md。自产片在本页工作台用预设出，onSite true，标档位，
 * 「Try this example」回填原图并切到出片的场景。
 *
 * R2 `home/showcase/ai-wedding/` 下 clips/{id}-720p.mp4、posters/{id}-720p.jpg、refs/{ref}.jpg；
 * 右栏和样片区用同一份 720p 文件。清单为空时样片区整个不渲染。
 */
export interface AiWeddingShowcaseClip {
	id: string;
	title: string;
	/** 一句话说明，JSON-LD VideoObject.description 与 <video> aria-label 用 */
	description: string;
	/** 出这条片用的画质档（只有自产片有），拼贴 hover 时显示档位名 */
	quality?: AiWeddingQualityId;
	/** 出这条片用的场景；「Try this example」顺带切过去 */
	template?: AiWeddingTemplateId;
	orientation: "landscape" | "portrait";
	/** 站内生成的才 true；false 的不进 VideoObject，文案也不能说是本站出的 */
	onSite: boolean;
	duration: number;
	videoUrl: string;
	poster: string;
	/** 成片带音轨：拼贴右上角出静音开关 */
	hasAudio: boolean;
	/** 原图（合照一张，或两张单人照），「Try this example」填进工作台的槽 */
	inputImages?: string[];
	/** 外部片的来源（只记档案，页面上不显示） */
	source?: string;
}

const R2_BASE = "home/showcase/ai-wedding";

/**
 * 视频和 poster 地址的版本参数（assets 域边缘缓存一天，连 404 也缓存）；换片子时加一
 */
const CLIP_VERSION = 1;

type Spec = Omit<
	AiWeddingShowcaseClip,
	"videoUrl" | "poster" | "inputImages"
> & {
	/** 自产片的原图（refs/ 下的文件名，或别的页面 refs 的完整 R2 路径） */
	refs?: string[];
};

/**
 * 顺序即拼贴顺序，第一条是右栏 Example。自产片 2026-10-29 本页 HD 出（Seedance 2.0 Mini 720p 5 秒 9:16，41 积分）；
 * 外部片 2026-10-29 抓取自 animatephoto.io/video-effects/ai-wedding（原文件 2026-06-26-4btpbb04 / 2026-06-26-s4hzdnri）
 */
const SPECS: Spec[] = [
	{
		id: "garden-couple",
		title: "Garden Wedding Kiss from One Couple Photo",
		description:
			"One couple photo becomes a golden-hour garden wedding: a white gown and veil, a dark suit, a floral arch and a kiss under falling petals.",
		quality: "hd",
		template: "garden",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: ["cafe-couple"],
	},
	{
		id: "beach-two",
		title: "Beach Wedding from Two Selfies",
		description:
			"Two separate selfies become one sunset beach ceremony: the bride in a satin gown and veil, the groom in a dark suit, kissing under a floral arch.",
		quality: "hd",
		template: "beach",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: [
			"home/showcase/burning-bridges/refs/satin-woman-1.jpg",
			"home/showcase/burning-bridges/refs/cardigan-man-1.jpg",
		],
	},
	// 11/01 补：右栏样片跟 tab 走，Chapel 原来没有自己的片
	{
		id: "chapel-couple",
		title: "Chapel Wedding Kiss from One Couple Photo",
		description:
			"One couple photo becomes a chapel wedding: a white gown and veil, a dark suit, sunlit chapel windows and a kiss as white petals fall.",
		quality: "hd",
		template: "chapel",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: ["home/showcase/ai-kiss/refs/couple-1.jpg"],
	},
	{
		id: "ext-blush",
		title: "Blush Gown Garden Wedding",
		description:
			"A couple in a blush tulle gown and a white tuxedo jacket smiles together on a lawn lined with white flowers.",
		orientation: "portrait",
		onSite: false,
		duration: 4,
		hasAudio: false,
		source: "animatephoto.io (2026-06-26-4btpbb04)",
	},
	{
		id: "ext-garden-walk",
		title: "Garden Ceremony Portrait",
		description:
			"The groom steps in beside the bride in a white gown for a garden wedding portrait by the ceremony chairs.",
		orientation: "portrait",
		onSite: false,
		duration: 4,
		hasAudio: false,
		source: "animatephoto.io (2026-06-26-s4hzdnri)",
	},
];

const refUrl = (ref: string) =>
	buildR2AssetsImageUrl(
		ref.startsWith("home/") ? ref : `${R2_BASE}/refs/${ref}.jpg`,
	);

export const aiWeddingShowcaseClips: AiWeddingShowcaseClip[] = SPECS.map(
	({ refs, ...spec }) => ({
		...spec,
		videoUrl: buildR2AssetsImageUrl(
			`${R2_BASE}/clips/${spec.id}-720p.mp4?v=${CLIP_VERSION}`,
		),
		poster: buildR2AssetsImageUrl(
			`${R2_BASE}/posters/${spec.id}-720p.jpg?v=${CLIP_VERSION}`,
		),
		inputImages: refs?.map(refUrl),
	}),
);

/** 样片上传日期，喂 VideoObject.uploadDate（完整 ISO 8601 带时区） */
export const AI_WEDDING_SHOWCASE_UPLOADED_AT = "2026-10-29T00:00:00+08:00";
